import {AbstractSettings, AbstractSettingsDef} from "./AbstractSettings";
import { SettingsRegistry } from "./SettingsRegistry";

export interface MapSettingsDef extends AbstractSettingsDef {
    //unauth
    google_api_key: string;
    google_maps_url: string;
    version: string;

    //auth
    always_show_maps: boolean;
    use_legacy_google_api: boolean;
    distance_calc_vendor?: string;
    request_location_vendor?: string;
    findnear_vendor?: string;
    google_greedy?: boolean;
    movement_map_vendor?: string;
    view_movement_vendor?: string;
    single_point_vendor?: string;
    mc_position_vendor?: string;
    partial_shipment_vendor?: string;
    zone_map?: string;
    trimble_settings?: {
        api_key1?: string;
        api_key2?: string;
    }
    google_map_type_id: string;

    // Route colors based on the combination of map provider and route provider
    route_colors?: {
        // Key format: "mapVendor_routeVendor"
        google_google: string;    // Google route on Google Map
        google_trimble: string;   // Trimble route on Google Map
        google_milemaker: string; // MileMaker route on Google Map
        trimble_trimble: string;  // Trimble route on Trimble Map
    }

    route_config?: {
        showGoogleRoute: boolean;
        showGoogleToggle: boolean;
        showMileMakerRoute: boolean;
        showMileMakerToggle: boolean;
    }
}

export class MapSettings extends AbstractSettings {
    constructor() {
        super("map_settings");
    }

    public static getSingleton(): MapSettings {
        return SettingsRegistry.get().getObject("map_settings") as MapSettings;
    }

    public static get(): MapSettingsDef {
        return MapSettings.getSingleton().values as MapSettingsDef;
    }

    public override get values(): MapSettingsDef {
        return super.values as MapSettingsDef;
    }

    protected getValuesInternal(): MapSettingsDef {
        return this._values as MapSettingsDef;
    }

    public getDistanceCalcVendor(): string {
        return this.values?.distance_calc_vendor;
    }

    public getZoneMapVendor(): string {
        return this.values?.zone_map;
    }

    public userGoogleMapTypeId(): string {
        return this.values?.google_map_type_id;
    }

    public noDistanceCalcVendor(): boolean {
        return this.getDistanceCalcVendor() === "N";
    }

    public getTrimbleApiKey(): string{
        const values = this.values;
        const key1 = values?.trimble_settings.api_key1;
        const key2 = values?.trimble_settings.api_key2;
        return key1 ? key1 : key2;
    }

    public getMovementMapVendor(): string {
        return this.values.movement_map_vendor;
    }

    public static readonly MAP_VENDOR = {
        GOOGLE: 'google',
        TRIMBLE: 'trimble'
    };

    public static readonly ROUTE_VENDOR = {
        GOOGLE: 'google',
        TRIMBLE: 'trimble',
        MILEMAKER: 'milemaker',
        UNKNOWN: 'unknown'
    };

    private static readonly COLORS = {
        GOOGLE: '#0000FF',
        TRIMBLE: '#4A7CC1',
        ALTERNATE: '#FF0000',
        FALLBACK: '#FF0000'
    };

    // Default colors based on vendor combination
    public static readonly DEFAULT_ROUTE_COLORS = {
        google_google: MapSettings.COLORS.GOOGLE,           // Google routes on Google Maps
        google_trimble: MapSettings.COLORS.ALTERNATE,       // Trimble routes on Google Maps
        google_milemaker: MapSettings.COLORS.ALTERNATE,     // MileMaker routes on Google Maps
        trimble_trimble: MapSettings.COLORS.TRIMBLE,        // Trimble routes on Trimble Maps
        google_unknown: MapSettings.COLORS.FALLBACK,        // Unknown routes on Google Maps
    };

    /**
     * Gets the appropriate route color based on both the map vendor and route vendor
     * @param mapVendor The vendor rendering the map (google, trimble)
     * @param routeVendor The vendor providing the route data (google, trimble, milemaker)
     * @returns The appropriate color for this combination
     */
    public getRouteColor(mapVendor: string, routeVendor: string): string {
        const key = `${mapVendor.toLowerCase()}_${routeVendor.toLowerCase()}`;
        const colors = this.values.route_colors || MapSettings.DEFAULT_ROUTE_COLORS;
        return colors[key] || MapSettings.DEFAULT_ROUTE_COLORS.google_google;
    }

    // Add default route config
    public static readonly DEFAULT_ROUTE_CONFIG = {
        showGoogleRoute: false,
        showGoogleToggle: false,
        showMileMakerRoute: true,
        showMileMakerToggle: true,
        showTrimbleRoute: true,  // Show PC*Miler routes by default
        showTrimbleToggle: true  // Show PC*Miler toggle by default
    };

    /**
     * Gets the route configuration settings, with defaults applied
     * @returns Route configuration object
     */
    public getRouteConfig(): typeof MapSettings.DEFAULT_ROUTE_CONFIG {
        // Start with defaults
        const baseConfig = { ...MapSettings.DEFAULT_ROUTE_CONFIG };

        // Override with any specific route config from settings
        return {
            ...baseConfig,
            ...(this.values.route_config || {})
        };
    }
}
